import type { AttachmentLink, ExecutionTask, TaskActivity, TaskComment } from "@/engine/types";

export const DEV_FEED_MOCK_PREFIX = "dev-mock-";

export function isDevFeedMockId(id: string): boolean {
  return id.startsWith(DEV_FEED_MOCK_PREFIX);
}

function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function svgThumb(bg: string, label: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160"><rect width="100%" height="100%" fill="${bg}"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#fff" font-size="16" font-family="sans-serif">${label}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export type DevTaskFeedMock = {
  comments: TaskComment[];
  activity: TaskActivity[];
  attachments: AttachmentLink[];
};

/** 무한 스크롤·탭 뱃지 확인용. 실제 Task에는 쓰지 않는다. */
export function buildDevTaskFeedMock(taskId: string): DevTaskFeedMock {
  const p = `${DEV_FEED_MOCK_PREFIX}${taskId}-`;
  const authors = ["김PM", "이아키", "최기획", "박현업", "정개발", "한디자인"];

  const attachments: AttachmentLink[] = [
    { id: `${p}img-1`, title: "와이어프레임-v3.png", url: svgThumb("#4f6bed", "WF v3"), addedAt: hoursAgo(40), kind: "image", size: 186_400, mimeType: "image/svg+xml" },
    { id: `${p}img-2`, title: "대시보드-캡처.png", url: svgThumb("#2d9f6f", "Dashboard"), addedAt: hoursAgo(36), kind: "image", size: 244_800, mimeType: "image/svg+xml" },
    { id: `${p}img-3`, title: "오류-재현.gif", url: svgThumb("#c43c3c", "Bug repro"), addedAt: hoursAgo(28), kind: "image", size: 512_000, mimeType: "image/svg+xml" },
    { id: `${p}img-4`, title: "모바일-목업.png", url: svgThumb("#8a5cf6", "Mobile"), addedAt: hoursAgo(18), kind: "image", size: 198_200, mimeType: "image/svg+xml" },
    { id: `${p}file-1`, title: "요구사항정의서-v2.pdf", url: "https://example.com/docs/req-v2.pdf", addedAt: hoursAgo(52), kind: "file", size: 1_248_000, mimeType: "application/pdf" },
    { id: `${p}file-2`, title: "인터페이스-목록.xlsx", url: "https://example.com/docs/if-list.xlsx", addedAt: hoursAgo(44), kind: "file", size: 86_400, mimeType: "application/vnd.ms-excel" },
    { id: `${p}file-3`, title: "회의록-1018.docx", url: "https://example.com/docs/minutes.docx", addedAt: hoursAgo(30), kind: "file", size: 42_100, mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
    { id: `${p}file-4`, title: "로그-샘플.zip", url: "https://example.com/docs/logs.zip", addedAt: hoursAgo(12), kind: "file", size: 3_210_000, mimeType: "application/zip" },
    { id: `${p}link-1`, title: "Figma 보드", url: "https://www.figma.com/file/dev-mock/board", addedAt: hoursAgo(26), kind: "link" },
    { id: `${p}link-2`, title: "Linear 이슈", url: "https://linear.app/dev/issue/WIT-128", addedAt: hoursAgo(14), kind: "link" },
    { id: `${p}link-3`, title: "Notion 스펙", url: "https://www.notion.so/dev-mock-spec", addedAt: hoursAgo(8), kind: "link" },
    { id: `${p}link-4`, title: "PR #482", url: "https://github.com/example/wit-tracker/pull/482", addedAt: hoursAgo(3), kind: "link" },
  ];

  const roots: TaskComment[] = [
    { id: `${p}c01`, author: authors[0]!, body: "현업 리뷰 일정을 이번 주 목요일로 잡아 두었습니다. 사전 자료는 첨부 확인 부탁드립니다.", at: hoursAgo(50), attachments: [attachments[4]!] },
    { id: `${p}c02`, author: authors[2]!, body: "회의록 초안 올렸습니다. 결정 사항만 먼저 봐 주세요.", at: hoursAgo(46), attachments: [attachments[6]!] },
    { id: `${p}c03`, author: authors[1]!, body: "아키텍처 대안 비교표는 온프레미스/클라우드/하이브리드 3안으로 정리했습니다.", at: hoursAgo(42) },
    { id: `${p}c04`, author: authors[4]!, body: "API 스펙 v3 기준으로 엔드포인트 목록을 다시 맞췄습니다. 3번 응답값만 확인이 필요합니다.", at: hoursAgo(38) },
    { id: `${p}c05`, author: authors[3]!, body: "생산팀은 20일 오후만 가능하다고 합니다. 화상 참여로 진행해도 될까요?", at: hoursAgo(34) },
    { id: `${p}c06`, author: authors[5]!, body: "와이어프레임 v3입니다. 모바일 하단 탭 위치를 오른쪽으로 옮겼습니다.", at: hoursAgo(31), attachments: [attachments[0]!, attachments[3]!] },
    { id: `${p}c07`, author: authors[0]!, body: "", at: hoursAgo(29), deleted: true, deletedAt: hoursAgo(20) },
    { id: `${p}c08`, author: authors[4]!, body: "재현 캡처입니다. 필터를 두 번 바꾸면 목록이 비는 현상이 있습니다.", at: hoursAgo(27), attachments: [attachments[2]!] },
    { id: `${p}c09`, author: authors[2]!, body: "주간 보고 초안에 GAP 결정 사항을 한 줄로 넣었습니다. 문구만 손봐 주세요.", at: hoursAgo(24) },
    { id: `${p}c10`, author: authors[1]!, body: "MES 실시간 연동은 배치 폴백을 남겨 두는 편이 안전해 보입니다.", at: hoursAgo(21) },
    { id: `${p}c11`, author: authors[3]!, body: "구매·재무는 화상 참여로 확정했습니다. 회의실 예약은 그대로 유지합니다.", at: hoursAgo(17) },
    { id: `${p}c12`, author: authors[5]!, body: "대시보드 캡처 올립니다. KPI 카드 순서를 일정/원가/리스크 순으로 바꿨습니다.", at: hoursAgo(15), attachments: [attachments[1]!] },
    { id: `${p}c13`, author: authors[0]!, body: "요구사항 정의서 서명 라운드를 목요일 오후에 넣었습니다.", at: hoursAgo(13), updatedAt: hoursAgo(11) },
    { id: `${p}c14`, author: authors[4]!, body: "로그 샘플과 PR 링크입니다. 재현 단계는 본문에 적었습니다.", at: hoursAgo(10), attachments: [attachments[7]!] },
    { id: `${p}c15`, author: authors[2]!, body: "Figma 보드에 최신 플로우를 올려 두었습니다. 댓글 스레드로 피드백 주세요.", at: hoursAgo(8) },
    { id: `${p}c16`, author: authors[1]!, body: "인터페이스 목록을 엑셀로 정리했습니다. 레거시 WMS 항목만 빈칸입니다.", at: hoursAgo(6) },
    { id: `${p}c17`, author: authors[3]!, body: "현장 일정은 다음 주 화요일 오전이 가장 여유 있습니다.", at: hoursAgo(4) },
    { id: `${p}c18`, author: authors[5]!, body: "모바일 목업 마지막 장입니다. 빈 상태 카피를 짧게 다듬었습니다.", at: hoursAgo(2), attachments: [attachments[3]!] },
    { id: `${p}c19`, author: authors[0]!, body: "오늘 오후 스탠드업에서 첨부 탭 밀도만 한 번 더 보겠습니다.", at: hoursAgo(1) },
    ...Array.from({ length: 42 }, (_, i) => ({
      id: `${p}c-extra-${String(i + 20).padStart(2, "0")}`,
      author: authors[i % authors.length]!,
      body: `목업 댓글 ${i + 20} — 이전 댓글 더보기(50건) 확인용입니다.`,
      at: hoursAgo(60 + i),
    })),
  ];

  const replies: TaskComment[] = [
    { id: `${p}r01`, parentId: `${p}c01`, author: authors[3]!, body: "목요일 14시로 맞춰 두었습니다. 사전 자료는 오전까지 회신하겠습니다.", at: hoursAgo(48) },
    { id: `${p}r02`, parentId: `${p}c01`, author: authors[0]!, body: "감사합니다. 결정 사항은 정의서에 바로 반영하겠습니다.", at: hoursAgo(47) },
    { id: `${p}r03`, parentId: `${p}c06`, author: authors[4]!, body: "탭을 오른쪽으로 옮기면 한 손 조작이 편해 보입니다. 이대로 가시죠.", at: hoursAgo(30) },
    { id: `${p}r04`, parentId: `${p}c07`, author: authors[2]!, body: "알겠습니다. 후속은 새 스레드에서 이어가겠습니다.", at: hoursAgo(19) },
    { id: `${p}r05`, parentId: `${p}c08`, author: authors[1]!, body: "필터 상태를 URL에 남기면 재현이 쉬워질 것 같습니다.", at: hoursAgo(25) },
    { id: `${p}r06`, parentId: `${p}c12`, author: authors[0]!, body: "원가 카드를 두 번째로 둔 구성이 더 읽힙니다.", at: hoursAgo(14) },
    { id: `${p}r07`, parentId: `${p}c18`, author: authors[2]!, body: "빈 상태 카피 ‘아직 항목이 없습니다’로 통일할까요?", at: hoursAgo(1.5) },
  ];

  const activity: TaskActivity[] = [
    { id: `${p}a01`, at: hoursAgo(55), actor: authors[0]!, message: "상태를 ‘진행 중’으로 변경함" },
    { id: `${p}a02`, at: hoursAgo(53), actor: authors[0]!, message: "담당자에 이아키를 추가함" },
    { id: `${p}a03`, at: hoursAgo(49), actor: authors[2]!, message: "마감일을 10/23으로 변경함" },
    { id: `${p}a04`, at: hoursAgo(41), actor: authors[1]!, message: "우선순위를 ‘중요’로 변경함" },
    { id: `${p}a05`, at: hoursAgo(35), actor: authors[0]!, message: "버킷을 ‘이번 주’로 옮김" },
    { id: `${p}a06`, at: hoursAgo(32), actor: authors[5]!, message: "첨부 추가: 와이어프레임-v3.png" },
    { id: `${p}a07`, at: hoursAgo(23), actor: authors[4]!, message: "라벨 ‘버그’를 추가함" },
    { id: `${p}a08`, at: hoursAgo(16), actor: authors[3]!, message: "체크리스트 ‘회의실 예약’을 완료함" },
    { id: `${p}a09`, at: hoursAgo(9), actor: authors[2]!, message: "첨부 추가: 회의록-1018.docx" },
    { id: `${p}a10`, at: hoursAgo(5), actor: authors[1]!, message: "연결된 WBS를 변경함" },
    { id: `${p}a11`, at: hoursAgo(2.5), actor: authors[0]!, message: "설명을 수정함" },
    { id: `${p}a12`, at: hoursAgo(0.8), actor: authors[4]!, message: "첨부 추가: PR #482" },
  ];

  return {
    comments: [...roots, ...replies],
    activity,
    attachments,
  };
}

export function mergeDevFeedMock(task: ExecutionTask, mock: DevTaskFeedMock): ExecutionTask {
  return {
    ...task,
    comments: [...task.comments, ...mock.comments],
    activity: [...task.activity, ...mock.activity],
    attachments: [...task.attachments, ...mock.attachments],
  };
}

export function stripDevFeedMockAttachments(attachments: AttachmentLink[]): AttachmentLink[] {
  return attachments.filter((a) => !isDevFeedMockId(a.id));
}
